/**
 * ProposalContactPicker — inline select + "Add New" popover for choosing
 * the primary recipient contact on a proposal. Saves the contact back to
 * the client (and site, if provided) via addClientContact.
 *
 * #171 (Group H): the picker's *index-based* selection lives in
 * component-local state instead of being derived from `value` on every
 * render. When two contacts share an email (or name), deriving the
 * index from `value` via `findIndex` would always snap back to the
 * first match — making the second duplicate row "unselectable". We
 * sync from `value` only on prop-change boundaries (initial mount,
 * explicit reset by parent) so clicking a duplicate stays sticky.
 *
 * Extracted from `pages/Proposals.jsx` to slim the file (was 2,302 lines
 * before — this extract drops it by ~75 lines).
 */
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { AddContactInline } from "./AddContactInline";

export function ProposalContactPicker({ clientId, siteId, contacts, value, onChange, onContactAdded }) {
  const [adding, setAdding] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  // `value` may be the contact's id, email or name — match any so contacts
  // without an email don't snap back to "None" after being picked.
  const matches = (c, v) => !!c && !!v && (c.id === v || (c.email && c.email === v) || c.name === v);
  useEffect(() => {
    if (!value) {
      setSelectedIdx(-1);
      return;
    }
    // Only re-derive when the current index no longer points at a
    // contact matching `value` (parent explicitly switched).
    if (selectedIdx >= 0 && matches(contacts[selectedIdx], value)) return;
    const i = contacts.findIndex(x => matches(x, value));
    setSelectedIdx(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, contacts]);
  const triggerValue = selectedIdx >= 0 ? `idx-${selectedIdx}` : "__none__";
  return (
    <div className="space-y-1.5">
      <Select
        value={triggerValue}
        onValueChange={(v) => {
          if (v === "__add__") { setAdding(true); return; }
          if (v === "__none__") { setSelectedIdx(-1); onChange(null); return; }
          const idx = parseInt(v.replace("idx-", ""), 10);
          if (!Number.isFinite(idx)) return;
          setSelectedIdx(idx);
          onChange(contacts[idx] || null);
        }}
        disabled={!clientId}
      >
        <SelectTrigger className="h-9 text-sm" data-testid="select-proposal-contact">
          <SelectValue placeholder={clientId ? "Select contact" : "Select customer first"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None —</SelectItem>
          {contacts.map((c, i) => (
            <SelectItem key={c.id || `${c.email || c.name}-${i}`} value={`idx-${i}`}>
              {c.name || "—"}{c.is_primary ? " (Primary)" : ""}
            </SelectItem>
          ))}
          <SelectItem value="__add__" className="text-blue-600 font-medium">+ Add New Contact…</SelectItem>
        </SelectContent>
      </Select>
      {adding && (
        <div className="relative">
          <AddContactInline
            clientId={clientId}
            siteId={siteId}
            onAdded={(c) => {
              onContactAdded(c);
              setAdding(false);
            }}
          />
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
            data-testid="cancel-add-contact"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
